import React, { useCallback } from "react";
import { Accordion, AccordionSummary, AccordionDetails, Box, Button, CircularProgress, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import { keyBy } from "Helpers/utils";
import { SETTING_TABLE_NAME } from "Constants/settingsTableName";
import ResponseTimeSettingsForm from "./Form";
import { useMsal } from "Auth/msal-context";
import { useKeyboard } from "../../../hooks/KeyboardContext";
import useSettings from "../../../hooks/useSettings";

const transformResponse = (res) => {
  if (res.jsonData && Array.isArray(res.jsonData)) {
    return keyBy(res.jsonData, "name");
  }
  return res.jsonData ?? {};
};

const ResponseTimeSettings = () => {
  const { t } = useTranslation("common");
  const { isAuthorized, isEdge } = useMsal();
  const { registerInput } = useKeyboard();

  const { formState, setFormState, loading, error, fetchSettings, saveSettings } =
    useSettings(SETTING_TABLE_NAME.RESPONSE_TIME_SETTINGS, transformResponse);

  const handleOnChange = useCallback((value, name, field) => {
    setFormState((prev) => prev ? {
      ...prev,
      [name]: { ...prev[name], [field]: value },
    } : prev);
  }, [setFormState]);

  const inputSelected = (e, name) => {
    const [compName, field] = name.split("|");
    registerInput(name, e.target.value, (v) => handleOnChange(v, compName, field));
  };

  const handleSave = () => {
    if (!formState) return;
    const payload = Object.keys(formState).map((key) => formState[key]);
    saveSettings(payload);
  };

  return (
    <Accordion sx={{ mb: 2 }} disableGutters onChange={(e, expanded) => { if (expanded) fetchSettings(); }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: "primary.main", color: "common.white" }}>
        <Typography sx={{ fontWeight: "bold", fontSize: "1.5rem" }}>Response Times</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ bgcolor: "background.light", p: 2 }}>
        {loading && <CircularProgress size={24} />}
        {error && (
          <Typography sx={{ textAlign: "center", color: "secondary.main" }}>{t("No Settings Found")}</Typography>
        )}
        {!loading && formState && (
          <ResponseTimeSettingsForm
            responseTimeSettings={formState}
            handleOnChange={handleOnChange}
            inputSelected={inputSelected}
          />
        )}
        {(isAuthorized || isEdge) && formState && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button variant="contained" size="small" onClick={handleSave}>{t("Save")}</Button>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default ResponseTimeSettings;
